import { FeedbackSubmitHandler, useFeedbackFlowContextSelector } from "@/components/Feedback/FeedbackFlowContext";
import { FeedbackEvents } from "@/components/Feedback/feedbackPopupContent";
import { Flex, FlexColumn, FlexColumnAlignJustifyCenter, FlexRowAlignCenter } from "@/components/Flex";
import { themeColors } from "@/utils/themeColors";
import styles from "./feedbackRatingSelector.module.css";
import toast from "react-hot-toast";
import { useState } from "react";
import clsx from "clsx";

const ratingColors = [themeColors.errorPrimary, "#E79950", "#D2C550", "#50D265", "#50D2C2"];

const FeedbackRatingSelector = ({
    title,
    onSubmitHandler,
    feedbackFor,
    className,
    id,
}: {
    title?: string;
    onSubmitHandler: FeedbackSubmitHandler;
    feedbackFor: FeedbackEvents;
    id?: string;
    className?: string;
}) => {
    const openFeedbackPopup = useFeedbackFlowContextSelector(state => state.openFeedbackPopup);
    const submissions = useFeedbackFlowContextSelector(state => state.submissions);
    const setSubmissions = useFeedbackFlowContextSelector(state => state.setSubmissions);

    const [hoveringIndex, setHoveringIndex] = useState<number | null>(null);

    const onRateHandler = (rating: number) => () => {
        if (rating < 5) {
            openFeedbackPopup({
                feedbackFor,
                rating,
                onSubmitHandler: ({ ...props }) => {
                    onSubmitHandler(props);
                    setSubmissions(prev => ({
                        ...prev,
                        [feedbackFor + id]: rating,
                    }));
                },
            });
        } else {
            toast.success("Thank you for your feedback!");
            setSubmissions(prev => ({
                ...prev,
                [feedbackFor + id]: rating,
            }));
            onSubmitHandler({ rating, reason: "", category: "", followUp: "" });
        }
    };

    if (submissions[feedbackFor + id]) {
        return (
            <FlexColumnAlignJustifyCenter
                style={{
                    height: "auto",
                    width: "100%",
                    padding: "2.4rem",
                    textAlign: "center",
                    backgroundColor: themeColors.neutralWhite,
                    borderRadius: "2rem",
                }}>
                {submissions[feedbackFor + id] < 5
                    ? "Thank you for giving us your feedback -- we’ll use this to improve Knowt!"
                    : "Thank you! We’re so happy you love using Knowt! 🫶"}
            </FlexColumnAlignJustifyCenter>
        );
    }

    return (
        <div className={clsx(styles.containerWrapper, className)}>
            <Flex
                className={styles.container}
                style={{
                    backgroundColor: themeColors.neutralWhite,
                }}>
                {title && <p className={clsx(styles.title, "bodyBold1")}>{title}</p>}
                <FlexColumn className={styles.ratingContainer}>
                    <FlexRowAlignCenter
                        style={{
                            gap: "1.6rem",
                        }}>
                        <p className={clsx(styles.labelInline, "secondaryText1")} style={{ fontWeight: 600 }}>
                            Not a fan
                        </p>
                        <FlexRowAlignCenter
                            style={{
                                gap: "0rem",
                                borderRadius: "2rem",
                                overflow: "hidden",
                                minWidth: "30rem",
                                width: "100%",
                                maxWidth: "60rem",
                            }}>
                            {[1, 2, 3, 4, 5].map((_, i) => (
                                <FlexRowAlignCenter
                                    as="button"
                                    className="strippedBtn"
                                    key={`feedback-rating-${i}`}
                                    onClick={onRateHandler(i + 1)}
                                    onMouseEnter={() => setHoveringIndex(i)}
                                    onMouseLeave={() => setHoveringIndex(null)}
                                    style={{
                                        backgroundColor: ratingColors[i],
                                        filter: hoveringIndex === i ? "brightness(1.21)" : "brightness(1)",
                                        transition: "filter 0.1s ease-in-out",
                                        padding: "0.8rem 1rem",
                                        width: "20%",
                                        height: "2.8rem",
                                        color: "white",
                                        justifyContent: "center",
                                    }}>
                                    {i + 1}
                                </FlexRowAlignCenter>
                            ))}
                        </FlexRowAlignCenter>
                        <p className={clsx(styles.labelInline, "secondaryText1")} style={{ fontWeight: 600 }}>
                            Love it
                        </p>
                    </FlexRowAlignCenter>
                    <FlexRowAlignCenter
                        style={{
                            width: "100%",
                            justifyContent: "space-between",
                        }}>
                        <p className={clsx(styles.labelBottom, "secondaryText1")} style={{ fontWeight: 600 }}>
                            Not a fan
                        </p>
                        <p className={clsx(styles.labelBottom, "secondaryText1")} style={{ fontWeight: 600 }}>
                            Love it
                        </p>
                    </FlexRowAlignCenter>
                </FlexColumn>
            </Flex>
        </div>
    );
};

export default FeedbackRatingSelector;
